import { Component, Input, ElementRef } from "@angular/core";
import { OrderDataImpl, OrderIntent } from "shared/cubiq-model-multi-asset";
import { ColumnFilterType, OrdersTableDefinitionFactory } from "shared/cubiq-services";
import { BfxColDef } from "shared/cubiq-services/model/TableDefinition";
import { ColDef, GridOptions } from "ag-grid-community";
import { NativeDesktopSupport } from "../bfx-native-desktop/native-desktop-support";
import {
  COLUMN_ORDER_ID,
  COLUMN_ORDER_STATE,
  COLUMN_REMAINING_AMOUNT,
  COLUMN_ORDER_REQUEST_RATE,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { OrdersTableMode } from "shared/cubiq-services/model/OrdersTableMode";

import { CALC_WS } from "shared/cubiq-services/model/BaseTableDefinitionFactory";
import { GammaAmountRenderer } from "./renderers/GammaAmountRenderer";
import { COLUMN_CLIENT_INTENT } from "shared/cubiq-services/model/ScratchPadTableDefinitionFactory.constants";
import { GammaAmountValue } from "./model/GammaAmountValue";
import { CommonIntentRenderer } from "app/bfx-blotter-orders/renderers/CommonIntentRenderer";

@Component({
  selector: `bfx-gamma-rungs-panel`,
  templateUrl: "./html/bfx-gamma-rungs-panel.html",
  standalone: false,
})
export class BfxGammaRungsPanelComponent {
  public columnDefs: Array<ColDef | BfxColDef> = [];
  public gridOptions: GridOptions;

  private _rungs: Array<OrderDataImpl>;

  constructor(
    private ordersTableDefinitionFactory: OrdersTableDefinitionFactory,
    private element: ElementRef,
  ) {
    this.initialize();
  }

  protected initialize(): void {
    this.gridOptions = <GridOptions>{
      frameworkComponents: {
        dirRenderer: CommonIntentRenderer,
        orderAmtRenderer: GammaAmountRenderer,
      },

      enableSorting: false,
      suppressContextMenu: true,
      headerHeight: 24,
      showToolPanel: false,
      suppressRowDrag: true,
      toolPanelSuppressSideButtons: true,
      enableFilter: false,
      rowDeselection: true,
      enableBrowserTooltips: true,
      onGridReady: (params) => {
        this.gridOptions.api.setRowData(this._rungs);
        params.api.sizeColumnsToFit();
      },
      onGridSizeChanged: (params) => {
        params.api.sizeColumnsToFit();
      },
    };

    const defn = this.ordersTableDefinitionFactory.createDef();

    // Direction: Buy or Sell
    let dirCol: BfxColDef = {
      suppressMenu: true,
      cellClass: ["ag-cell-default"],
    };
    dirCol.field = COLUMN_CLIENT_INTENT;
    dirCol.headerName = "Dir";
    dirCol.minWidth = dirCol.width = CALC_WS(4);
    dirCol.cellRenderer = "dirRenderer";
    dirCol.cellClass = ["blotter_center_align_col blotter_priority_col intent_col"];
    dirCol.columnFilterType = ColumnFilterType.NONE;
    dirCol.columnFilterData = OrderIntent;

    // dealt entered amount
    const orderAmtCol: BfxColDef = {
      suppressMenu: true,
      cellClass: ["ag-cell-default"],
    };
    orderAmtCol.headerName = "Order Amt";
    orderAmtCol.field = COLUMN_REMAINING_AMOUNT;
    orderAmtCol.minWidth = 80;
    orderAmtCol.width = CALC_WS(7);
    orderAmtCol.cellClass = ["blotter_amt_align"];
    orderAmtCol.cellRenderer = "orderAmtRenderer";
    orderAmtCol.valueGetter = function (params): GammaAmountValue {
      let order: OrderDataImpl = params.data;
      if (order == null) {
        return null;
      }
      return {
        orderAmt: order.leavesQty ? order.leavesQty.toFormat(0) : "-",
        orderAmtUsd: (order.usdEquivLeavesQty ? order.usdEquivLeavesQty.toFormat(0) : "-") + " USD",
        orderAmtUsdInvalid: false,
      };
    };

    const stateCol = Object.assign({}, defn.getColumnDefinition(COLUMN_ORDER_STATE));
    stateCol.width = CALC_WS(8);

    this.columnDefs = [
      dirCol,
      orderAmtCol,
      Object.assign({}, defn.getColumnDefinition(COLUMN_ORDER_REQUEST_RATE)),
      Object.assign({}, defn.getColumnDefinition(COLUMN_ORDER_ID)),
      stateCol,
    ];

    // Stop columns from sorting or being dragged
    for (const colDef of this.columnDefs) {
      colDef.sortable = false;
      colDef.suppressMovable = true;
    }

    this.gridOptions.context = {
      mode: OrdersTableMode.DEALT,
    };
    NativeDesktopSupport.prepareGridOptions(this.gridOptions, this.element.nativeElement);
  }

  @Input()
  public set rungs(rungs: Array<OrderDataImpl>) {
    this._rungs = rungs;
    if (this.gridOptions.api) {
      this.gridOptions.api.setRowData(this._rungs);
    }
  }
  public get rungs(): Array<OrderDataImpl> {
    return this._rungs;
  }
}
